import { Stylis, hashString, memoize, unitless } from 'emotion-utils';
import stylisRuleSheet from 'stylis-rule-sheet';

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

/*

high performance StyleSheet for css-in-js systems

- uses multiple style tags behind the scenes for millions of rules
- uses `insertRule` for appending in production for *much* faster performance
- 'polyfills' on server side

// usage

import StyleSheet from 'glamor/lib/sheet'
let styleSheet = new StyleSheet()

styleSheet.inject()
- 'injects' the stylesheet into the page (or into memory if on server)

styleSheet.insert('#box { border: 1px solid red; }')
- appends a css rule into the stylesheet

styleSheet.flush()
- empties the stylesheet of all its contents

*/

function sheetForTag(tag) {
  if (tag.sheet) {
    return tag.sheet;
  }

  // this weirdness brought to you by firefox
  for (var i = 0; i < document.styleSheets.length; i++) {
    if (document.styleSheets[i].ownerNode === tag) {
      return document.styleSheets[i];
    }
  }
}

function makeStyleTag() {
  var tag = document.createElement('style');
  tag.type = 'text/css';
  tag.setAttribute('data-emotion', '');
  tag.appendChild(document.createTextNode(''));
  document.head.appendChild(tag);
  return tag;
}

var StyleSheet = function () {
  function StyleSheet() {
    classCallCheck(this, StyleSheet);

    this.isBrowser = typeof window !== 'undefined';
    this.isSpeedy = process.env.NODE_ENV === 'production'; // the big drawback here is that the css won't be editable in devtools
    this.tags = [];
    this.ctr = 0;
  }

  StyleSheet.prototype.inject = function inject() {
    if (this.injected) {
      throw new Error('already injected!');
    }
    if (this.isBrowser) {
      this.tags[0] = makeStyleTag();
    } else {
      // server side 'polyfill'. just enough behavior to be useful.
      this.sheet = [];
    }
    this.injected = true;
  };

  StyleSheet.prototype.speedy = function speedy(bool) {
    if (this.ctr !== 0) {
      // cannot change speedy mode after inserting any rule to sheet. Either call speedy(${bool}) earlier in your app, or call flush() before speedy(${bool})
      throw new Error('cannot change speedy now');
    }
    this.isSpeedy = !!bool;
  };

  StyleSheet.prototype.insert = function insert(rule, sourceMap) {
    if (this.isBrowser) {
      // this is the ultrafast version, works across browsers
      if (this.isSpeedy) {
        var tag = this.tags[this.tags.length - 1];
        var sheet = sheetForTag(tag);

        try {
          sheet.insertRule(rule, sheet.cssRules.length);
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('illegal rule', rule); // eslint-disable-line no-console
          }
        }
      } else {
        var _tag = makeStyleTag();
        this.tags.push(_tag);
        _tag.appendChild(document.createTextNode(rule + (sourceMap || '')));
      }
      this.ctr++;
      if (this.ctr % 65000 === 0) {
        this.tags.push(makeStyleTag());
      }
    } else {
      // enough 'spec compliance' to be able to extract the rules later
      // in other words, just the rule
      this.sheet.push(rule);
    }
  };

  StyleSheet.prototype.flush = function flush() {
    if (this.isBrowser) {
      this.tags.forEach(function (tag) {
        return tag.parentNode.removeChild(tag);
      });
      this.tags = [];
      this.ctr = 0;
      // todo - look for remnants in document.styleSheets
    } else {
      // simpler on server
      this.sheet = [];
    }
    this.injected = false;
  };

  return StyleSheet;
}();

var sheet = new StyleSheet();

// 🚀
sheet.inject();
var stylisOptions = { keyframe: false };

if (process.env.NODE_ENV !== 'production') {
  stylisOptions.compress = false;
}

var stylis = new Stylis(stylisOptions);

var externalStylisPlugins = [];

var use = stylis.use;

function insertRule(rule) {
  sheet.insert(rule, currentSourceMap);
}

var insertionPlugin = stylisRuleSheet(insertRule);

var useStylisPlugin = function useStylisPlugin(plugin) {
  externalStylisPlugins.push(plugin);
  use(null)(externalStylisPlugins)(insertionPlugin);
};

var registered = {};

var inserted = {};

var currentSourceMap = '';

stylis.use(insertionPlugin);

function handleInterpolation(interpolation, couldBeSelectorInterpolation) {
  if (interpolation == null) {
    return '';
  }

  switch (typeof interpolation) {
    case 'boolean':
      return '';
    case 'function':
      return handleInterpolation.call(this, this === undefined ? interpolation() : interpolation(this.mergedProps, this.context), couldBeSelectorInterpolation);
    case 'object':
      return createStringFromObject.call(this, interpolation);
    default:
      var cached = registered[interpolation];
      return couldBeSelectorInterpolation === false && cached !== undefined ? cached : interpolation;
  }
}

var hyphenateRegex = /[A-Z]|^ms/g;

var processStyleName = memoize(function (styleName) {
  return styleName.replace(hyphenateRegex, '-$&').toLowerCase();
});

var processStyleValue = function processStyleValue(key, value) {
  if (value === undefined || value === null || typeof value === 'boolean') return '';

  if (unitless[key] !== 1 && !isNaN(value) && value !== 0) {
    return value + 'px';
  }
  return value;
};

var objectToStringCache = new WeakMap();

function createStringFromObject(obj) {
  if (objectToStringCache.has(obj)) {
    return objectToStringCache.get(obj);
  }
  var string = '';

  if (Array.isArray(obj)) {
    obj.forEach(function (interpolation) {
      string += handleInterpolation.call(this, interpolation, false);
    }, this);
  } else {
    Object.keys(obj).forEach(function (key) {
      if (typeof obj[key] !== 'object') {
        if (registered[obj[key]] !== undefined) {
          string += key + '{' + registered[obj[key]] + '}';
        } else {
          string += processStyleName(key) + ':' + processStyleValue(key, obj[key]) + ';';
        }
      } else {
        string += key + '{' + handleInterpolation.call(this, obj[key], false) + '}';
      }
    }, this);
  }
  objectToStringCache.set(obj, string);

  return string;
}

function isLastCharDot(string) {
  return string.charCodeAt(string.length - 1) === 46; // .
}

var hash = void 0;
var name = void 0;

var labelPattern = /label:\s*([^\s;\n]+)\s*[;\n]/g;

function createStyles(strings) {
  var stringMode = true;
  var styles = '';
  var identifierName = '';

  if (strings == null || strings.raw === undefined) {
    stringMode = false;
    styles = handleInterpolation.call(this, strings, false);
  } else {
    styles = strings[0];
  }

  for (var _len = arguments.length, interpolations = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    interpolations[_key - 1] = arguments[_key];
  }

  interpolations.forEach(function (interpolation, i) {
    styles += handleInterpolation.call(this, interpolation, isLastCharDot(styles));
    if (stringMode === true && strings[i + 1] !== undefined) {
      styles += strings[i + 1];
    }
  }, this);
  styles = styles.replace(labelPattern, function (match, p1) {
    identifierName += '-' + p1;
    return '';
  });
  hash = hashString(styles + identifierName);
  name = hash + identifierName;
  return styles;
}

if (process.env.NODE_ENV !== 'production') {
  var sourceMapRegEx = /\/\*#\ssourceMappingURL=data:application\/json;\S+\s+\*\//;
  var oldStylis = stylis;
  stylis = function stylis(selector, styles) {
    var result = sourceMapRegEx.exec(styles);
    currentSourceMap = result ? result[0] : '';
    oldStylis(selector, styles);
    currentSourceMap = '';
  };
}

function css() {
  var styles = createStyles.apply(this, arguments);
  var selector = 'css-' + name;

  if (registered[selector] === undefined) {
    registered[selector] = styles;
  }

  if (inserted[hash] === undefined) {
    stylis('.' + selector, styles);
    inserted[hash] = true;
  }

  return selector;
}

function keyframes() {
  var styles = createStyles.apply(this, arguments);
  var animation = 'animation-' + name;

  if (inserted[hash] === undefined) {
    stylis('', '@keyframes ' + animation + '{' + styles + '}');
    inserted[hash] = true;
  }
  return animation;
}

function injectGlobal() {
  var styles = createStyles.apply(this, arguments);
  if (inserted[hash] === undefined) {
    stylis('', styles);
    inserted[hash] = true;
  }
}

function fontFace() {
  var styles = createStyles.apply(undefined, arguments);
  if (inserted[hash] === undefined) {
    stylis('', '@font-face{' + styles + '}');
    inserted[hash] = true;
  }
}

function getRegisteredStyles(registeredStyles, classNames) {
  var rawClassName = '';

  classNames.split(' ').forEach(function (className) {
    if (registered[className] !== undefined) {
      registeredStyles.push(className);
    } else {
      rawClassName += className + ' ';
    }
  });
  return rawClassName;
}

function merge(className, sourceMap) {
  var registeredStyles = [];

  var rawClassName = getRegisteredStyles(registeredStyles, className);

  if (registeredStyles.length < 2) {
    return className;
  }
  return rawClassName + css(registeredStyles, sourceMap);
}

function classnames() {
  var len = arguments.length;
  var i = 0;
  var cls = '';
  for (; i < len; i++) {
    var arg = arguments[i];

    if (arg == null) continue;
    var next = cls && cls + ' ' || cls;

    switch (typeof arg) {
      case 'boolean':
        break;
      case 'function':
        cls = next + classnames(arg());
        break;
      case 'object':
        {
          if (Array.isArray(arg)) {
            cls = next + classnames.apply(null, arg);
          } else {
            for (var k in arg) {
              if (arg[k]) {
                cls && (cls += ' ');
                cls += k;
              }
            }
          }
          break;
        }
      default:
        {
          cls = next + arg;
        }
    }
  }
  return cls;
}

function cx() {
  return merge(classnames.apply(undefined, arguments));
}

function hydrate(ids) {
  ids.forEach(function (id) {
    inserted[id] = true;
  });
}

function flush() {
  sheet.flush();
  inserted = {};
  registered = {};
  sheet.inject();
}

export { sheet, useStylisPlugin, registered, inserted, css, keyframes, injectGlobal, fontFace, getRegisteredStyles, merge, cx, hydrate, flush };
//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/emotion/dist/index.es.js
// module id = 24
// module chunks = 0